import type { Theme } from "@mui/material/styles";

export interface DeleteConfirmationStyleParams {
	theme: Theme;
	materialColors: { outline: string; onSurface: string };
	alpha: (color: string, value: number) => string;
}

/**
 * Shared styles for delete confirmation blocks used in
 * EditPlantUserDialog, DeletePlantUserModal, DeleteSectorAdminModal.
 * When all access is removed, use the same message pattern as Delete Sector Admin.
 */
export function GetDeleteConfirmationStyles({
	theme,
	materialColors,
	alpha,
}: DeleteConfirmationStyleParams) {
	return {
		divider: {
			borderColor: alpha(materialColors.outline, 0.12),
		},
		confirmationBox: {
			display: "flex" as const,
			alignItems: "center" as const,
			mt: 1,
			marginLeft: theme.spacing(1),
		},
		checkbox: {
			color: materialColors.onSurface,
			padding: 0,
			marginRight: theme.spacing(1),
			"&.Mui-checked": {
				color: materialColors.onSurface,
			},
		},
		/** Use when message is destructive (e.g. "will lose access to MCC") */
		messageDestructive: {
			color: "error.main",
			lineHeight: 1.6,
			fontWeight: 700,
		},
		/** Use when message is normal confirmation text */
		messageNormal: {
			color: materialColors.onSurface,
			lineHeight: 1.6,
			fontWeight: 400,
		},
	};
}

/** Message text when user will lose all access to MCC (same pattern as Delete Sector Admin). */
export function GetLoseAccessToMccMessage(userName: string, sectorNames?: string): string {
	const base = `Performing this action will cause ${userName} to lose access to MCC.`;
	if (sectorNames && sectorNames.trim()) {
		return `${base.replace(/\.$/, "")} (sectors: ${sectorNames}).`;
	}
	return base;
}

/**
 * Message for remove-sectors confirmation (Edit Sector Admin / Edit Plant User).
 * Use when the current user is removing one or more sectors from another user.
 */
export function GetRemoveSectorsConfirmationMessage(
	wouldLoseMcc: boolean,
	userName: string,
	removedSectorNames: string[]
): string {
	const displayName = userName?.trim() || "the user";
	if (wouldLoseMcc) {
		return `I confirm that I want to remove all permissions for ${displayName}; they will be removed from MCC and will no longer have any access.`;
	}
	const sectorList = removedSectorNames.length > 0 ? removedSectorNames.join(", ") : "—";
	return `I understand that the user will no longer have access to the sectors: ${sectorList}.`;
}
